import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  ChatWindow,
  CloseIcon,
  IconButtonV2,
  Input,
  ModalV2 as Modal,
  toast,
} from '@utilities';
import {
  createChatMessage,
  createChatSession,
  readChatSession,
  readFlowState,
} from '@api/narrative-projects';
import { shareNarrativeWithClientGroup, shareNarrativeWithUsers } from '@api/narratives';

import NarrativeFlowView from '../../../../components/NarrativeFlowView';
import ModalShare from '../../../../components/ModalShare';
import {
  CHAT_RESPONSE_POLL_INTERVAL_MS,
  CHAT_RESPONSE_POLL_MAX_ATTEMPTS,
  DEFAULT_ASSISTANT_MESSAGE,
} from '../../utilities/constants';
import { deleteRunNarrative, renameRunNarrative } from '../../utilities/narrativeActions';
import { downloadNarrativeXlsx, useNarrativePptExport } from '../../utilities/useNarrativeExports';
import NarrativeCard from '../NarrativeCard';
import NarrativePptExportObserver from '../NarrativePptExportObserver';
import { EMPTY_NARRATIVES, NARRATIVE_CREATED_LABEL } from './utilities/constants';
import {
  formatNarrativeDate,
  isAssistantResponseMessage,
  mergeOptimisticNarratives,
} from './utilities/helpers';
import styles from './_index.module.scss';

const NarrativesChatWindow = ({
  activeFlowId = null,
  allowCanvasHydrationOnMount = true,
  chatSessionId: providedChatSessionId = null,
  initialErrorMessage = '',
  initialInput = '',
  initialInputValue = '',
  initialMessages = [],
  liveFlows = [],
  narrativeNamesById = {},
  onChatSessionReady,
  onCloseToolWindow,
  onFlowSelected,
  onFlowStateChange,
  onMessageSent,
  onNarrativeDeleted,
  onNarrativeNameChange,
  onStateSnapshot,
  optimisticNarratives = EMPTY_NARRATIVES,
  shouldBreakAwayFromRunOnMessage = false,
  shouldHydrateOnMount = true,
  syncCanvasOnMount = true,
}) => {
  const [chatSessionId, setChatSessionId] = useState(providedChatSessionId);
  const [messages, setMessages] = useState(() => initialMessages || []);
  const [inputValue, setInputValue] = useState(initialInputValue || '');
  const [isInitializing, setIsInitializing] = useState(!initialMessages?.length);
  const [isAwaitingAssistant, setIsAwaitingAssistant] = useState(false);
  const [errorMessage, setErrorMessage] = useState(initialErrorMessage || '');
  const [isDeletingNarrative, setIsDeletingNarrative] = useState(false);
  const [isRenamingNarrative, setIsRenamingNarrative] = useState(false);
  const [narrativePendingDelete, setNarrativePendingDelete] = useState(null);
  const [narrativePendingRename, setNarrativePendingRename] = useState(null);
  const [narrativeShareId, setNarrativeShareId] = useState(null);
  const [renamedNarrativeName, setRenamedNarrativeName] = useState('');
  const { handlePptExportError, handlePptExportReady, pendingPptExport, startPptExport } =
    useNarrativePptExport();

  const isMountedRef = useRef(true);
  const fallbackIdCounterRef = useRef(0);
  const emittedFlowIdRef = useRef(null);
  const emittedChatSessionRef = useRef(null);
  const hasSubmittedInitialInputRef = useRef(false);
  const hydratedProvidedSessionIdRef = useRef(null);
  const wasHydratedRef = useRef(Boolean(initialMessages?.length));
  const lastHydratedCanvasSnapshotRef = useRef(null);
  const assistantMessageCountRef = useRef(0);
  const pendingPollTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const createFallbackMessageId = useCallback((role) => {
    fallbackIdCounterRef.current += 1;
    return `${role}-message-${fallbackIdCounterRef.current}-${Date.now()}`;
  }, []);

  const wait = useCallback(
    (timeoutMs) =>
      new Promise((resolve) => {
        const timeoutId = window.setTimeout(() => {
          if (pendingPollTimeoutRef.current === timeoutId) {
            pendingPollTimeoutRef.current = null;
          }
          resolve();
        }, timeoutMs);

        pendingPollTimeoutRef.current = timeoutId;
      }),
    []
  );

  const buildCanvasSnapshot = useCallback((flowId, flowState) => {
    return JSON.stringify({
      flowId: flowId ?? null,
      flowState: flowState ?? null,
    });
  }, []);

  const hydrateMessages = useCallback(
    async (
      sessionId,
      { shouldBreakAwayFromRun = false, sourceOfChange = 'hydrate', syncCanvas = true } = {}
    ) => {
      if (!sessionId) return;

      try {
        const [messagesResult, canvasResult] = await Promise.allSettled([
          readChatSession({
            chatSessionId: sessionId,
            createFallbackId: createFallbackMessageId,
          }),
          syncCanvas
            ? readFlowState({
                chatSessionId: sessionId,
              })
            : Promise.resolve({ flowId: null, flowState: null }),
        ]);

        if (messagesResult.status !== 'fulfilled') {
          throw messagesResult.reason;
        }

        const { messages: nextMessages } = messagesResult.value;
        let nextCanvasState = { flowId: null, flowState: null };
        let didReadCanvasState = !syncCanvas;

        if (syncCanvas && canvasResult.status === 'fulfilled') {
          nextCanvasState = canvasResult.value;
          didReadCanvasState = true;
        } else if (syncCanvas && canvasResult.status === 'rejected') {
          console.error('Failed to read flow state', canvasResult.reason);
        }

        const nextFlowId = nextCanvasState?.flowId ?? null;
        const nextFlowState = nextCanvasState?.flowState ?? null;
        const hydratedMessagesWithoutOptimistic =
          initialInput && nextMessages.length === 0
            ? [
                ...nextMessages,
                {
                  content: initialInput,
                  id: createFallbackMessageId('user'),
                  role: 'user',
                },
              ]
            : nextMessages;
        const hydratedMessages = mergeOptimisticNarratives(
          hydratedMessagesWithoutOptimistic,
          optimisticNarratives
        );
        const hasMatchingRunFlowMessage =
          nextFlowId != null &&
          hydratedMessages.some(
            (message) =>
              message?.type === 'run_flow' &&
              String(message?.narrative?.flowId) === String(nextFlowId)
          );
        const shouldSelectHydratedRun =
          hasMatchingRunFlowMessage && String(nextFlowId) !== String(activeFlowId);
        const shouldBreakAwayFromHydratedRun =
          Boolean(shouldBreakAwayFromRun) && !shouldSelectHydratedRun;

        if (isMountedRef.current) {
          wasHydratedRef.current = true;
          setMessages(hydratedMessages);
          setErrorMessage('');

          const nextCanvasSnapshot = buildCanvasSnapshot(nextFlowId, nextFlowState);
          const hasCanvasChanged = lastHydratedCanvasSnapshotRef.current !== nextCanvasSnapshot;

          lastHydratedCanvasSnapshotRef.current = nextCanvasSnapshot;

          if (syncCanvas && didReadCanvasState && hasCanvasChanged) {
            if (onFlowStateChange) {
              onFlowStateChange(nextFlowState || null, {
                shouldBreakAwayFromRun: shouldBreakAwayFromHydratedRun,
                sourceOfChange: sourceOfChange,
              });
            }

            if (
              !shouldBreakAwayFromHydratedRun &&
              onFlowSelected &&
              nextFlowId &&
              nextFlowId !== emittedFlowIdRef.current
            ) {
              emittedFlowIdRef.current = nextFlowId;
              onFlowSelected(nextFlowId, { source: 'chat-hydration' });
            }
          }
        }

        return {
          flowId: nextFlowId,
          flowState: nextFlowState,
          messages: hydratedMessages,
        };
      } catch (error) {
        console.error('Failed to read chat session', error);
        if (isMountedRef.current) {
          setErrorMessage('Unable to load your chat history. Please try again.');
        }
        throw error;
      }
    },
    [
      activeFlowId,
      buildCanvasSnapshot,
      createFallbackMessageId,
      initialInput,
      onFlowSelected,
      onFlowStateChange,
      optimisticNarratives,
    ]
  );

  const initializeSession = useCallback(async () => {
    if (providedChatSessionId) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);
    setErrorMessage('');

    try {
      const sessionId = await createChatSession();
      if (!sessionId) throw new Error('Chat service did not return a session identifier.');

      if (!isMountedRef.current) return;

      setChatSessionId(sessionId);
      emittedChatSessionRef.current = sessionId;
      if (onChatSessionReady) {
        onChatSessionReady(sessionId);
      }
      await hydrateMessages(sessionId);
    } catch (error) {
      console.error('Failed to initialize chat session', error);
      if (isMountedRef.current) {
        setErrorMessage('We could not start a chat session. Please refresh and try again.');
      }
    } finally {
      if (isMountedRef.current) setIsInitializing(false);
    }
  }, [hydrateMessages, onChatSessionReady, providedChatSessionId]);

  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  useEffect(() => {
    if (!providedChatSessionId) return;
    setChatSessionId(providedChatSessionId);
    emittedChatSessionRef.current = providedChatSessionId;
    if (onChatSessionReady) {
      onChatSessionReady(providedChatSessionId);
    }
    if (!shouldHydrateOnMount) {
      hydratedProvidedSessionIdRef.current = providedChatSessionId;
      wasHydratedRef.current = true;
      setIsInitializing(false);
      return;
    }
    if (hydratedProvidedSessionIdRef.current === providedChatSessionId) {
      setIsInitializing(false);
      return;
    }

    hydratedProvidedSessionIdRef.current = providedChatSessionId;
    setIsInitializing(true);
    hydrateMessages(providedChatSessionId, {
      syncCanvas: syncCanvasOnMount && allowCanvasHydrationOnMount,
    })
      .catch(() => {})
      .finally(() => {
        if (isMountedRef.current) setIsInitializing(false);
      });
  }, [
    allowCanvasHydrationOnMount,
    providedChatSessionId,
    hydrateMessages,
    onChatSessionReady,
    shouldHydrateOnMount,
    syncCanvasOnMount,
  ]);

  useEffect(() => {
    if (!chatSessionId || emittedChatSessionRef.current === chatSessionId) return;
    emittedChatSessionRef.current = chatSessionId;
    if (onChatSessionReady) {
      onChatSessionReady(chatSessionId);
    }
  }, [chatSessionId, onChatSessionReady]);

  const handleSubmit = useCallback(
    async (userInput) => {
      if (!chatSessionId) return;

      const trimmedInput = userInput?.trim();
      if (!trimmedInput) return;

      setIsAwaitingAssistant(true);
      setErrorMessage('');
      setInputValue('');

      try {
        await createChatMessage({ chatSessionId, userInput: trimmedInput });

        const existingAssistantMessageCount = assistantMessageCountRef.current;
        let attempts = 0;
        let hasCompletedAssistantReply = false;

        while (!hasCompletedAssistantReply && attempts < CHAT_RESPONSE_POLL_MAX_ATTEMPTS) {
          if (!isMountedRef.current) break;

          const hydratedSession = await hydrateMessages(chatSessionId, {
            shouldBreakAwayFromRun: shouldBreakAwayFromRunOnMessage,
            sourceOfChange: 'chat',
          });
          const hydratedMessages = Array.isArray(hydratedSession?.messages)
            ? hydratedSession.messages
            : [];
          const assistantMessages = hydratedMessages.filter(isAssistantResponseMessage);
          const hasNewAssistantReply = assistantMessages.length > existingAssistantMessageCount;
          const hasStreamingAssistantReply = assistantMessages.some((message) =>
            Boolean(message?.isStreaming)
          );

          hasCompletedAssistantReply = hasNewAssistantReply && !hasStreamingAssistantReply;
          if (hasCompletedAssistantReply) break;

          attempts += 1;
          if (attempts >= CHAT_RESPONSE_POLL_MAX_ATTEMPTS) break;
          await wait(CHAT_RESPONSE_POLL_INTERVAL_MS);
        }

        if (hasCompletedAssistantReply && onMessageSent) {
          onMessageSent();
        }
      } catch (error) {
        console.error('Failed to create chat message', error);
        if (isMountedRef.current) {
          toast.error('Unable to send your question. Please try again.');
          setInputValue(trimmedInput);
        }
      } finally {
        if (isMountedRef.current) setIsAwaitingAssistant(false);
      }
    },
    [chatSessionId, hydrateMessages, onMessageSent, shouldBreakAwayFromRunOnMessage, wait]
  );

  useEffect(() => {
    return () => {
      if (pendingPollTimeoutRef.current) {
        window.clearTimeout(pendingPollTimeoutRef.current);
        pendingPollTimeoutRef.current = null;
      }
    };
  }, []);

  const displayedMessages = useMemo(() => {
    if (messages.length) return messages;

    if (errorMessage) {
      return [{ content: errorMessage, id: 'assistant-error', role: 'assistant' }];
    }

    if (isInitializing) {
      return [
        {
          content: 'Setting up your Narratives chat...',
          id: 'assistant-initializing',
          role: 'assistant',
        },
      ];
    }

    return [DEFAULT_ASSISTANT_MESSAGE];
  }, [messages, errorMessage, isInitializing]);

  useEffect(() => {
    assistantMessageCountRef.current = messages.filter(isAssistantResponseMessage).length;
  }, [messages]);

  useEffect(() => {
    setMessages((previousMessages) =>
      mergeOptimisticNarratives(previousMessages, optimisticNarratives)
    );
  }, [optimisticNarratives]);

  useEffect(() => {
    if (!Object.keys(narrativeNamesById).length) return;

    setMessages((previousMessages) => {
      let didChange = false;
      const nextMessages = previousMessages.map((message) => {
        if (message?.type !== 'run_flow' || message?.narrative?.flowId == null) return message;

        const syncedName = narrativeNamesById[message.narrative.flowId];
        if (typeof syncedName !== 'string' || syncedName === message.narrative.name) return message;

        didChange = true;
        return { ...message, narrative: { ...message.narrative, name: syncedName } };
      });

      return didChange ? nextMessages : previousMessages;
    });
  }, [narrativeNamesById]);

  const liveFlowsById = useMemo(() => {
    if (!Array.isArray(liveFlows)) return new Map();

    return new Map(
      liveFlows.filter((flow) => flow?.id != null).map((flow) => [String(flow.id), flow])
    );
  }, [liveFlows]);

  const updateNarrativeMessage = useCallback((flowId, updates) => {
    setMessages((previousMessages) =>
      previousMessages.map((message) =>
        message?.type === 'run_flow' && Number(message?.narrative?.flowId) === Number(flowId)
          ? { ...message, narrative: { ...message.narrative, ...updates } }
          : message
      )
    );
  }, []);

  const handleRenameNarrative = useCallback(async () => {
    const flowId = narrativePendingRename?.flowId;
    const nextName = renamedNarrativeName.trim();
    if (flowId == null || !nextName || isRenamingNarrative) return;

    setIsRenamingNarrative(true);
    try {
      await renameRunNarrative({ flowId, name: nextName });
      updateNarrativeMessage(flowId, { name: nextName });
      onNarrativeNameChange?.({ chatSessionId, flowId, name: nextName });
      toast.success('Narrative renamed successfully.');
    } catch (error) {
      console.error('Failed to rename narrative', error);
      toast.error('Unable to rename narrative. Please try again.');
    } finally {
      setIsRenamingNarrative(false);
      setNarrativePendingRename(null);
      setRenamedNarrativeName('');
    }
  }, [
    chatSessionId,
    isRenamingNarrative,
    narrativePendingRename?.flowId,
    onNarrativeNameChange,
    renamedNarrativeName,
    updateNarrativeMessage,
  ]);

  const handleDeleteNarrative = useCallback(async () => {
    const flowId = narrativePendingDelete?.flowId;
    if (flowId == null || !chatSessionId || isDeletingNarrative) return;

    setIsDeletingNarrative(true);
    try {
      const isDeletingActiveNarrative = Number(activeFlowId) === Number(flowId);
      const { hasFlowStateCleanupError } = await deleteRunNarrative({
        chatSessionId,
        flowId,
        shouldClearFlowState: isDeletingActiveNarrative,
      });

      if (hasFlowStateCleanupError) {
        toast.error(
          'Narrative deleted, but we could not reset the canvas. Please refresh and try again.'
        );
      }
      if (isDeletingActiveNarrative) {
        onFlowSelected?.(null);
      }

      updateNarrativeMessage(flowId, { isDeleted: true });
      onNarrativeDeleted?.({
        chatSessionId,
        flowId,
        shouldClearCanvasState: isDeletingActiveNarrative,
        shouldClearRunFlowState: isDeletingActiveNarrative,
      });
      if (!hasFlowStateCleanupError) {
        toast.success('Narrative deleted successfully.');
      }
    } catch (error) {
      console.error('Failed to delete narrative from chat', error);
      toast.error('Unable to delete narrative. Please try again.');
    } finally {
      setIsDeletingNarrative(false);
      setNarrativePendingDelete(null);
    }
  }, [
    activeFlowId,
    chatSessionId,
    isDeletingNarrative,
    narrativePendingDelete?.flowId,
    onFlowSelected,
    onNarrativeDeleted,
    updateNarrativeMessage,
  ]);

  const renderChatMessage = useCallback(
    (message) => {
      if (message?.type === 'run_flow_created') return message.content;
      if (message?.type !== 'run_flow') return undefined;

      const narrative = message?.narrative;
      const narrativeFlowId = narrative?.flowId;
      if (narrativeFlowId == null) return null;
      const liveFlow = liveFlowsById.get(String(narrativeFlowId));
      const narrativeName = narrative?.name || 'Untitled Narrative';

      return (
        <div className={styles['chat-narrative-message']}>
          <div className={styles['chat-narrative-created-label']}>{NARRATIVE_CREATED_LABEL}</div>
          <NarrativeCard
            createdDatetime={formatNarrativeDate(narrative?.createdAt)}
            flowId={narrativeFlowId}
            isActive={String(activeFlowId) === String(narrativeFlowId)}
            isDeleted={Boolean(narrative?.isDeleted)}
            layout="chat"
            name={narrativeName}
            onDelete={() => {
              setNarrativePendingDelete(narrative);
            }}
            onDownloadPpt={() => {
              startPptExport(narrativeFlowId);
            }}
            onDownloadXlsx={() => {
              downloadNarrativeXlsx(narrativeFlowId);
            }}
            onOpen={() => onFlowSelected?.(narrativeFlowId, { source: 'chat-card' })}
            onRename={() => {
              setNarrativePendingRename(narrative);
              setRenamedNarrativeName(narrativeName);
            }}
            onShare={() => {
              setNarrativeShareId(narrativeFlowId);
            }}
            status={liveFlow?.status || narrative?.status}
            testIdPrefix="chat-narrative"
          />
        </div>
      );
    },
    [activeFlowId, liveFlowsById, onFlowSelected, startPptExport]
  );

  const isInputDisabled =
    isInitializing || !chatSessionId || Boolean(errorMessage) || isAwaitingAssistant;

  useEffect(() => {
    if (!initialInput || !chatSessionId || messages.length > 0) return;
    if (hasSubmittedInitialInputRef.current) return;

    hasSubmittedInitialInputRef.current = true;

    handleSubmit(initialInput);
  }, [initialInput, chatSessionId, handleSubmit, messages.length]);

  useEffect(() => {
    if (!onStateSnapshot) return;
    onStateSnapshot({
      chatSessionId,
      errorMessage,
      inputValue,
      messages,
      wasHydrated: wasHydratedRef.current,
    });
  }, [chatSessionId, errorMessage, inputValue, messages, onStateSnapshot]);

  return (
    <>
      <ChatWindow
        headerAction={
          onCloseToolWindow ? (
            <IconButtonV2
              aria-label="Close"
              icon={<CloseIcon />}
              onClick={onCloseToolWindow}
              size="medium"
              tooltip="Close"
              tooltipProps={{ placement: 'left' }}
              variant="default"
            />
          ) : null
        }
        headerTitle="Numerator Nexa"
        inputDisabled={isInputDisabled}
        inputValue={inputValue}
        isAgentStreaming={isAwaitingAssistant}
        messages={displayedMessages}
        onChangeInput={setInputValue}
        onSubmit={handleSubmit}
        renderMarkdown="assistant"
        renderMessage={renderChatMessage}
        submitDisabled={isInputDisabled}
      />
      {narrativePendingDelete ? (
        <Modal
          buttons={[
            <Button
              key="cancel-delete-chat-narrative"
              onClick={() => setNarrativePendingDelete(null)}
              text="Cancel"
              variant="secondary"
            />,
            <Button
              isDisabled={isDeletingNarrative}
              key="confirm-delete-chat-narrative"
              onClick={handleDeleteNarrative}
              text="Delete"
              variant="error"
            />,
          ]}
          data-testid="chat-narrative-delete-modal"
          isActive
          onClose={() => setNarrativePendingDelete(null)}
          title="Delete Narrative"
        >
          <p>
            Are you sure you want to delete “{narrativePendingDelete?.name || 'this narrative'}”?
            This action is permanent and cannot be undone.
          </p>
        </Modal>
      ) : null}
      {narrativePendingRename ? (
        <Modal
          buttons={[
            <Button
              key="cancel-rename-chat-narrative"
              onClick={() => {
                setNarrativePendingRename(null);
                setRenamedNarrativeName('');
              }}
              text="Cancel"
              variant="secondary"
            />,
            <Button
              isDisabled={
                isRenamingNarrative ||
                !renamedNarrativeName.trim() ||
                renamedNarrativeName.trim() === narrativePendingRename?.name
              }
              key="save-rename-chat-narrative"
              onClick={handleRenameNarrative}
              text="Save"
            />,
          ]}
          isActive
          onClose={() => {
            setNarrativePendingRename(null);
            setRenamedNarrativeName('');
          }}
          title="Rename Narrative"
        >
          <Input
            aria-label="Rename Narrative"
            className={styles['rename-narrative-input']}
            id="rename-chat-narrative-input"
            label="Narrative Name"
            onChange={setRenamedNarrativeName}
            value={renamedNarrativeName}
          />
        </Modal>
      ) : null}
      {narrativeShareId ? (
        <ModalShare
          ids={[narrativeShareId]}
          isNarrative
          isSurvey={false}
          setShowModalShare={(isOpen) => {
            if (!isOpen) setNarrativeShareId(null);
          }}
          shareItemWithClients={shareNarrativeWithClientGroup}
          shareItemWithUsers={shareNarrativeWithUsers}
        />
      ) : null}
      {pendingPptExport?.flowId ? (
        <div
          aria-hidden="true"
          className={styles['chat-narrative-ppt-export-container']}
          inert=""
          tabIndex={-1}
        >
          <NarrativeFlowView
            flowId={pendingPptExport.flowId}
            hidePageTitle
            hideTitle
            renderAfterContent={(hookData) => (
              <NarrativePptExportObserver
                hookData={hookData}
                onExportError={handlePptExportError}
                onExportReady={handlePptExportReady}
                request={pendingPptExport}
              />
            )}
            useWindowScroll={false}
          />
        </div>
      ) : null}
    </>
  );
};

NarrativesChatWindow.propTypes = {
  activeFlowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  allowCanvasHydrationOnMount: PropTypes.bool,
  chatSessionId: PropTypes.string,
  initialErrorMessage: PropTypes.string,
  initialInput: PropTypes.string,
  initialInputValue: PropTypes.string,
  initialMessages: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node,
      id: PropTypes.string,
      narrative: PropTypes.shape({
        createdAt: PropTypes.string,
        flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        isDeleted: PropTypes.bool,
        name: PropTypes.string,
        status: PropTypes.string,
      }),
      role: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  liveFlows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      status: PropTypes.string,
    })
  ),
  narrativeNamesById: PropTypes.objectOf(PropTypes.string),
  onChatSessionReady: PropTypes.func,
  onCloseToolWindow: PropTypes.func,
  onFlowSelected: PropTypes.func,
  onFlowStateChange: PropTypes.func,
  onMessageSent: PropTypes.func,
  onNarrativeDeleted: PropTypes.func,
  onNarrativeNameChange: PropTypes.func,
  onStateSnapshot: PropTypes.func,
  optimisticNarratives: PropTypes.arrayOf(
    PropTypes.shape({
      createdAt: PropTypes.string,
      flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      isDeleted: PropTypes.bool,
      name: PropTypes.string,
      status: PropTypes.string,
    })
  ),
  shouldBreakAwayFromRunOnMessage: PropTypes.bool,
  shouldHydrateOnMount: PropTypes.bool,
  syncCanvasOnMount: PropTypes.bool,
};

export default NarrativesChatWindow;
